import { Link } from "react-router-dom";
import Layout from "../components/site/Layout";
import Seo from "../components/Seo";
import Faq from "../components/Faq";

const ITEMS = [
  {
    q: "What is an EPS file?",
    a: "EPS (Encapsulated PostScript) is a vector graphics format used for logos, illustrations and print artwork. Rather than storing pixels, it stores the artwork as mathematical paths — so it can be scaled to any size, from a business card to a billboard, without ever losing sharpness. It dates from the late 1980s and remains common in professional print and branding work.",
  },
  {
    q: "Why won't my EPS file open when I double-click it?",
    a: "Because an EPS is not really an image — it is a small PostScript program describing how to draw the artwork, and displaying it means running that program through a PostScript interpreter. Neither Windows nor macOS includes one, so the file either does nothing or opens in a text editor showing raw code. Nothing is wrong with your file.",
  },
  {
    q: "How do I open an EPS file?",
    a: "Upload it to the EPS Viewer to see it instantly with nothing installed, or open it from inside software that supports the format — the free Inkscape or GIMP, or Adobe Illustrator and CorelDRAW if you have them. Inkscape and GIMP also need Ghostscript installed to read EPS.",
  },
  {
    q: "Is the EPS Viewer free?",
    a: "Yes. Viewing your file and downloading the result is completely free — no account, no watermark, no software to install and no trial period.",
  },
  {
    q: "Do I need Adobe Illustrator?",
    a: "No. The viewer renders your file for you, so you need no design software at all to view or convert an EPS. If you need to edit the artwork rather than just view it, Inkscape is a capable free alternative to Illustrator.",
  },
  {
    q: "Which formats can I convert my EPS to?",
    a: "PNG, JPG and PDF. Choose PNG for logos and anything needing a transparent background, JPG when file size matters more than crisp edges, and PDF when you want to keep the artwork as sharp vectors — which is what most print shops prefer.",
  },
  {
    q: "How do I convert EPS to PNG?",
    a: "Upload your EPS, wait a few seconds for the preview to appear, then click the PNG button beneath it. The rendering happens on our servers, which is why it works identically on any device, including phones.",
  },
  {
    q: "Are my files private and secure?",
    a: "Files are uploaded over an encrypted HTTPS connection and removed after your session — we do not share, sell or retain your artwork. If you are working with material under a strict NDA, a local tool such as Inkscape keeps the file on your own machine and is the safer choice for that case.",
  },
  {
    q: "Can I use the EPS Viewer on my phone?",
    a: "Yes. Because the rendering happens server-side, all you need is a modern mobile browser on Android or iOS. This is genuinely useful, since neither mobile platform has any native EPS support.",
  },
  {
    q: "Why does my EPS look blurry?",
    a: "Most likely you are seeing the low-resolution preview thumbnail that many EPS files embed so layout software can display something without running the PostScript. It is usually 72 DPI. The real vector data underneath is perfectly sharp, and rendering it properly proves that. If a converted image looks soft, export it again at a larger pixel size.",
  },
  {
    q: "Why did my transparent background turn white?",
    a: "JPG has no alpha channel, so transparency cannot be stored and is replaced with solid white. Export to PNG instead. Worth knowing too that EPS itself does not support live transparency — drop shadows and glows are already flattened when the EPS is written.",
  },
  {
    q: "Why do the colours look duller than the original?",
    a: "The file is almost certainly in CMYK, the four-ink model used for printing. Screens display RGB, which can show colours that ink cannot reproduce, so CMYK artwork always looks flatter on a monitor. This is normal and the printed result will look as intended.",
  },
  {
    q: "Why is the text in my EPS not editable?",
    a: "It was outlined before export, converting the letters into plain vector shapes. That is deliberate and standard practice for logos, because it guarantees the file renders correctly on machines that do not have the font. No software can convert it back into editable text.",
  },
  {
    q: "Does converting EPS reduce quality?",
    a: "Converting to PNG or JPG rasterizes the artwork at a fixed resolution, after which it can no longer be enlarged cleanly — so export at the size you actually need. Converting to PDF is effectively lossless, because both formats are vector. Either way, keep the original EPS as your master copy.",
  },
  {
    q: "What's the maximum file size?",
    a: "Typical logo and artwork files process comfortably. Very large EPS files containing high-resolution embedded photographs take longer, and extremely complex artwork with tens of thousands of paths can be slow to render.",
  },
  {
    q: "Can I convert a PNG or JPG back into an EPS?",
    a: "Not in any meaningful way. You can wrap the pixels inside an EPS container, but the artwork stays raster. Recovering true vectors means redrawing the artwork or auto-tracing it, and tracing only approximates the original — acceptable for simple high-contrast images, poor for photographs.",
  },
  {
    q: "Is EPS still used today?",
    a: "Yes, though it has largely been superseded by PDF for print and SVG for the web. Decades of logo archives, print templates and stock libraries exist as EPS, so anyone working with brand assets still encounters it regularly.",
  },
  {
    q: "Should I send my printer an EPS or a PDF?",
    a: "A print-ready PDF, in almost all cases. Most modern print workflows are built around PDF and some no longer accept EPS at all. Ask your printer whether they require a specific PDF/X profile, and whether the job needs bleed.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ITEMS.map((i) => ({
    "@type": "Question",
    name: i.q,
    acceptedAnswer: { "@type": "Answer", text: i.a },
  })),
};

export default function FaqPage() {
  return (
    <Layout>
      <Seo
        title="EPS Viewer FAQ – Common Questions About EPS Files"
        description="Answers to common questions about EPS files and the EPS Viewer: why EPS won't open, how to convert it, transparency and colour surprises, file safety, mobile support and print delivery."
        path="/faq"
        jsonLd={jsonLd}
      />
      <section className="section">
        <div className="container">
          <div className="section-head--center">
            <span className="eyebrow">Help center</span>
            <h1 className="section-title">Frequently asked questions</h1>
            <p className="section-lead">
              Everything you might want to know about EPS files and the EPS Viewer.
            </p>
          </div>

          <div className="guide__prose" style={{ maxWidth: 760, margin: "0 auto 32px" }}>
            <p>
              These are the questions we are actually asked, roughly in the order people
              ask them. Most arrive from someone who has just been sent an EPS by a
              designer or print shop and cannot open it — so if that is you, start with
              the first three.
            </p>
            <p>
              For longer explanations, our <Link to="/guides">guides</Link> cover each
              topic in depth, and the <Link to="/glossary">glossary</Link> defines the
              terminology without jargon.
            </p>
          </div>

          <Faq items={ITEMS} />

          <div className="guide__prose" style={{ maxWidth: 760, margin: "36px auto 0" }}>
            <p>
              Still stuck? <Link to="/contact">Send us the details</Link> and we will help.
              If a file fails to render, telling us which application produced it is
              usually the fastest route to an answer.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
